import { useCallback } from 'react';
import { getProject, useUpdateProject } from '@client/store/project';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { getSelectedVersion } from '@client/store/ui';
import { getProposal } from '@shared/helpers/proposals';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import { updateBuildingVersionProperties } from '@shared/helpers/project.helpers';
import type { IProposal } from '@shared/models/proposals.model';
import type {
  ReportProposalSlideKind,
  ReportSlideKind,
} from '@shared/models/report-slides.model';
import { withReportSlideHidden } from '../report-slide-visibility.helpers';

/**
 * Where a slide's hidden state lives: shared slides and baseline slides on the
 * version, everything else on the proposal that owns the page.
 */
export type ReportSlideRef =
  | { scope: 'version'; kind: ReportSlideKind }
  | {
      scope: 'proposal';
      proposalId: IProposal['id'];
      kind: ReportProposalSlideKind;
    };

/** A report page plus the slide its thumbnail eye toggles. */
export type ReportPageEntry = {
  key: string;
  node: React.ReactElement;
  slide: ReportSlideRef;
  isHidden: boolean;
};

/**
 * Persist show/hide of a report slide, on the version or on its proposal.
 *
 * Visibility is presentation-only — no result depends on it — so both paths skip
 * the enrichment worker. Without that, flipping the eye recomputed the whole
 * project and held the store in its loading state until the worker returned,
 * which made the toggle feel slow.
 */
export const useSetReportSlideHidden = () => {
  const updateProject = useUpdateProject();
  const applyChanges = useApplyProjectChanges({ skipEnrichment: true });

  return useCallback(
    (slide: ReportSlideRef, nextHidden: boolean) => {
      const selectedVersion = getSelectedVersion();
      if (!selectedVersion) return;

      if (slide.scope === 'version') {
        void updateProject({
          kind: 'snapshot',
          skipEnrichment: true,
          project: updateBuildingVersionProperties(getProject(), {
            id: selectedVersion.id,
            report_hidden_slides: withReportSlideHidden(
              selectedVersion.report_hidden_slides,
              slide.kind,
              nextHidden,
            ),
          }),
        });
        return;
      }

      const proposal = getProposal(selectedVersion, slide.proposalId);
      if (!proposal) return;
      void applyChanges([
        {
          type: ProjectChangeType.ProposalUpdate,
          versionId: selectedVersion.id,
          updates: [
            {
              id: slide.proposalId,
              report_hidden_slides: withReportSlideHidden(
                proposal.report_hidden_slides,
                slide.kind,
                nextHidden,
              ),
            },
          ],
        },
      ]);
    },
    [updateProject, applyChanges],
  );
};
